import { jsx as _jsx } from "react/jsx-runtime";
import React, { use } from 'react';
import MuiAvatar from '@mui/material/Avatar';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { Icon } from '@famly/mf_data-display_icon';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { useTooltipRequiredProps } from '@famly/mf_data-display_tooltip';
import { getFontSize } from './avatar-get-font-size';
import { AvatarGroupContext } from './avatar-group.context';
const DEFAULT_SIZE = 32;
export const Avatar = ({ alt, initials, size: sizeFromProps, src, color, disabled, placeholderType = 'person', ref, ...props }) => {
    var _a, _b;
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);
    const tooltipProps = useTooltipRequiredProps(props);
    const { size: sizeFromAvatarGroup } = use(AvatarGroupContext);
    const borderColor = props.borderColor;
    const borderWidth = (_a = props.borderWidth) !== null && _a !== void 0 ? _a : 1;
    const size = (_b = sizeFromProps !== null && sizeFromProps !== void 0 ? sizeFromProps : sizeFromAvatarGroup) !== null && _b !== void 0 ? _b : DEFAULT_SIZE;
    const { background, foreground } = getAvatarColors({ color, disabled });
    return (_jsx(MuiAvatar, { ref: ref, alt: alt, src: src, sx: theme => ({
            background: theme.modernFamlyTheme.colorPalette[background],
            color: theme.modernFamlyTheme.colorPalette[foreground],
            width: `${size}px`,
            height: `${size}px`,
            fontSize: `${getFontSize(size)}px`,
            lineHeight: 'unset',
            textTransform: 'uppercase',
            ...(borderColor && {
                borderWidth,
                borderStyle: 'solid',
                borderColor: theme.modernFamlyTheme.colorPalette[borderColor],
            }),
            ['.MuiAvatar-img']: {
                // Grayscale the profile image if the avatar is disabled
                filter: hasValue(src) && disabled === true ? 'grayscale(1)' : undefined,
                opacity: hasValue(src) && disabled === true ? '0.5' : undefined,
            },
        }), ...dataProps, ...ariaProps, ...tooltipProps, children: getFallback({ initials, size, placeholderType, disabled }) }));
};
Avatar.displayName = 'Avatar';
const getAvatarColors = (config) => {
    if (config.disabled === true) {
        return { background: 'n75', foreground: 'n200' };
    }
    switch (config.color) {
        case 'light-blue':
            return { background: 'b100', foreground: 'b400' };
        case 'blue':
            return { background: 'b200', foreground: 'b500' };
        case 'light-green':
            return { background: 'g100', foreground: 'g400' };
        case 'green':
            return { background: 'g200', foreground: 'g500' };
        case 'light-orange':
            return { background: 'o100', foreground: 'o400' };
        case 'orange':
            return { background: 'o200', foreground: 'o500' };
        case 'light-yellow':
            return { background: 'y100', foreground: 'y400' };
        case 'yellow':
            return { background: 'y200', foreground: 'o500' };
        default:
            return { background: 'p50', foreground: 'p300' };
    }
};
/**
 * Gets the fallback of the Avatar if image cannot be loaded. Fallback order:
 *
 * - `initials` (if provided)
 * - Icon based on placeholderType (person, room, or institution)
 *
 * @param config
 * @returns
 */
const getFallback = (config) => {
    if (config.initials) {
        return config.initials;
    }
    // Determine which icon to show based on placeholder type
    let iconName;
    if (config.placeholderType === 'room') {
        iconName = 'door_open';
    }
    else if (config.placeholderType === 'institution') {
        iconName = 'home';
    }
    else {
        iconName = 'person';
    }
    /**
     * Icon size based on linear regression to match initials scaling approach.
     * Samples: 32px avatar → 22px icon, 48px avatar → 30px icon, 96px avatar → 54px icon
     */
    const iconSize = Math.round(0.5 * config.size + 6);
    // Use n200 for disabled state, p300 for normal
    const iconColor = config.disabled ? 'n200' : 'p300';
    return _jsx(Icon, { name: iconName, filled: true, size: iconSize, color: iconColor });
};
